import { useState, useRef, useCallback, useEffect, ChangeEvent } from "react";
import { Mic, MicOff, Loader2, FileText, Sparkles, ChevronDown, ChevronUp, Pencil, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// RPM CPT codes auto-assigned based on session context
const RPM_CPT_CODES = [
  { code: "99453", label: "RPM Setup & Patient Education" },
  { code: "99454", label: "RPM Device Supply & Monitoring (30 days)" },
  { code: "99457", label: "RPM Clinical Staff Time (first 20 min)" },
  { code: "99458", label: "RPM Clinical Staff Time (additional 20 min)" },
  { code: "99091", label: "RPM Data Collection & Interpretation" },
];

interface SOAPNotes {
  chief_complaint: string;
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
}

interface SpeechNoteCaptureProps {
  patientId?: string;
  patientName?: string;
  sessionActive: boolean;
  microphoneId?: string;
  onSoapNotesChange?: (notes: SOAPNotes | null) => void;
}

export function SpeechNoteCapture({
  patientId,
  patientName = "Patient",
  sessionActive,
  microphoneId,
  onSoapNotesChange,
}: SpeechNoteCaptureProps) {
  const { user } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [liveText, setLiveText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const [isGeneratingSOAP, setIsGeneratingSOAP] = useState(false);
  const [soapNotes, setSoapNotes] = useState<SOAPNotes | null>(null);
  const [showSOAP, setShowSOAP] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Determine RPM CPT codes based on session context
  const assignedCptCodes = ["99457", "99091"];

  const startListening = useCallback(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Speech recognition is not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    let finalTranscript = transcript;

    recognition.onresult = (event: any) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += (finalTranscript ? " " : "") + text;
          setTranscript(finalTranscript);
        } else {
          interim += text;
        }
      }
      setLiveText(interim);
    };

    recognition.onerror = (event: any) => {
      if (event.error !== "aborted" && event.error !== "no-speech") {
        console.warn("Speech recognition error:", event.error);
      }
    };

    recognition.onend = () => {
      if (recognitionRef.current && isListening) {
        try {
          recognition.start();
        } catch {
          setIsListening(false);
          setLiveText("");
        }
      } else {
        setIsListening(false);
        setLiveText("");
      }
    };

    recognition.start();
    recognitionRef.current = recognition;
    setIsListening(true);
  }, [transcript, isListening]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsListening(false);
    setLiveText("");
  }, []);

  useEffect(() => {
    if (!sessionActive && isListening) {
      stopListening();
    }
  }, [sessionActive, isListening, stopListening]);

  const generateSOAPNotes = async () => {
    if (!transcript.trim()) {
      toast.error("No transcript to summarize. Capture some speech first.");
      return;
    }

    setIsGeneratingSOAP(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-soap-notes", {
        body: { transcript: transcript.trim(), patientName },
      });

      if (error) throw error;

      const notes = data as SOAPNotes;
      setSoapNotes(notes);
      setShowSOAP(true);
      onSoapNotesChange?.(notes);
      toast.success("SOAP notes generated successfully!");
    } catch (err: any) {
      console.error("SOAP generation error:", err);
      toast.error(`Failed to generate SOAP notes: ${err.message}`);
    } finally {
      setIsGeneratingSOAP(false);
    }
  };

  const saveNotesToPatientProfile = async () => {
    if (!transcript.trim() && !soapNotes) {
      toast.error("No notes to save.");
      return;
    }
    if (!user || !patientId) {
      toast.error("Missing user or patient context.");
      return;
    }

    setIsSaving(true);
    try {
      const servicesData = assignedCptCodes.map((code) => {
        const cpt = RPM_CPT_CODES.find((c) => c.code === code);
        return {
          id: `rpm-${code}`,
          label: cpt?.label || code,
          category: "Remote Monitoring",
        };
      });

      // Build note text with SOAP structure if available
      let noteText = "[Auto-generated from Telehealth Session]\n\n";
      if (soapNotes) {
        noteText += `CHIEF COMPLAINT:\n${soapNotes.chief_complaint}\n\n`;
        noteText += `SUBJECTIVE:\n${soapNotes.subjective}\n\n`;
        noteText += `OBJECTIVE:\n${soapNotes.objective}\n\n`;
        noteText += `ASSESSMENT:\n${soapNotes.assessment}\n\n`;
        noteText += `PLAN:\n${soapNotes.plan}\n\n`;
        noteText += `---\nRaw Transcript:\n${transcript.trim()}`;
      } else {
        noteText += transcript.trim();
      }

      const { error } = await supabase.from("patient_notes").insert({
        patient_id: patientId,
        provider_id: user.id,
        note_text: noteText,
        services: servicesData,
        cpt_codes: assignedCptCodes,
        billing_synced: false,
      });

      if (error) throw error;

      toast.success("Session notes saved to patient profile with RPM billing codes.");
      setAutoSaved(true);
    } catch (err: any) {
      toast.error(`Failed to save notes: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-1.5">
            <FileText className="h-4 w-4" />
            Session Notes
          </CardTitle>
          <div className="flex items-center gap-2">
            {isListening && (
              <Badge variant="default" className="text-xs gap-1 animate-pulse">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                </span>
                Listening
              </Badge>
            )}
            <Button
              size="sm"
              variant={isListening ? "destructive" : "outline"}
              onClick={isListening ? stopListening : startListening}
              disabled={!sessionActive}
              className="h-7 text-xs gap-1"
            >
              {isListening ? (
                <><MicOff className="h-3.5 w-3.5" /> Stop</>
              ) : (
                <><Mic className="h-3.5 w-3.5" /> Auto-Capture</>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-2 overflow-auto">
        {/* Live transcription indicator */}
        {isListening && liveText && (
          <div className="rounded-md bg-primary/5 border border-primary/20 p-2">
            <p className="text-xs text-muted-foreground italic">{liveText}</p>
          </div>
        )}

        {/* Editable transcript */}
        <Textarea
          placeholder={
            sessionActive
              ? "Click 'Auto-Capture' to start speech detection, or type notes manually..."
              : "Start the session to begin capturing notes..."
          }
          value={transcript}
          onChange={(e) => { setTranscript(e.target.value); setAutoSaved(false); setSoapNotes(null); }}
          className="flex-1 min-h-[80px] text-sm resize-none"
          disabled={!sessionActive && !transcript}
        />

        {/* AI SOAP Generation */}
        <Button
          size="sm"
          variant="secondary"
          onClick={generateSOAPNotes}
          disabled={isGeneratingSOAP || !transcript.trim()}
          className="w-full gap-1.5"
        >
          {isGeneratingSOAP ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating SOAP Notes...</>
          ) : (
            <><Sparkles className="h-3.5 w-3.5" /> Generate SOAP Notes with AI</>
          )}
        </Button>

        {/* SOAP Notes Display */}
        {soapNotes && (
          <div className="space-y-1.5">
            <button
              onClick={() => setShowSOAP(!showSOAP)}
              className="flex items-center gap-1 text-xs font-medium text-primary hover:underline w-full"
            >
              {showSOAP ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              AI-Generated SOAP Notes
              <Badge variant="outline" className="ml-auto text-[10px] px-1.5 py-0">AI</Badge>
            </button>
            {showSOAP && (
              <div className="rounded-md border bg-muted/30 p-3 space-y-2 text-xs">
                {(["chief_complaint", "subjective", "objective", "assessment", "plan"] as const).map((field) => {
                  const labels: Record<string, string> = {
                    chief_complaint: "Chief Complaint",
                    subjective: "Subjective",
                    objective: "Objective",
                    assessment: "Assessment",
                    plan: "Plan",
                  };
                  return (
                    <div key={field}>
                      <p className="font-semibold text-foreground mb-0.5">{labels[field]}</p>
                      <Textarea
                        value={soapNotes[field]}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                          setSoapNotes((prev) => {
                            const next = prev ? { ...prev, [field]: e.target.value } : prev;
                            if (next) onSoapNotesChange?.(next);
                            return next;
                          });
                          setAutoSaved(false);
                        }}
                        className="text-xs min-h-[40px] resize-none bg-background"
                      />
                    </div>
                  );
                })}
                <p className="text-[10px] text-muted-foreground italic flex items-center gap-1">
                  <Pencil className="h-2.5 w-2.5" /> Edit fields above before saving
                </p>
              </div>
            )}
          </div>
        )}

        {/* RPM billing codes */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">RPM Billing Codes (Auto-assigned)</p>
          <div className="flex flex-wrap gap-1">
            {assignedCptCodes.map((code) => {
              const cpt = RPM_CPT_CODES.find((c) => c.code === code);
              return (
                <Badge key={code} variant="outline" className="text-xs font-mono">
                  {code} — {cpt?.label}
                </Badge>
              );
            })}
          </div>
        </div>

        {/* Save button */}
        <Button
          size="sm"
          onClick={saveNotesToPatientProfile}
          disabled={isSaving || (!transcript.trim() && !soapNotes) || autoSaved}
          className="w-full"
        >
          {isSaving ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</>
          ) : autoSaved ? (
            "✓ Saved to Patient Profile"
          ) : (
            "Save Notes to Patient Profile"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
